import React from 'react';
import { Dropdown, Image, Table } from 'react-bootstrap';
import Lottie from 'react-lottie';
import { connect, useDispatch } from 'react-redux';

import AuthButton from 'components/base/AuthButton';
import CustomListComponent from 'components/base/CustomListComponent';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import BasePrompt from 'components/base/prompt/BasePrompt';
import AddTableTimeRange from 'components/dashboard/AddTableTimeRange';
import MetricTableBody from 'components/dashboard/MetricTableBody';
import MetricTableSideBar from 'components/dashboard/MetricTableSideBar';
import StoreSelectDropdown from 'components/transactions/StoreSelectDropdown';
import { ImportResource } from 'models/DownloadImportModel';

import { createAction } from 'utils/index';

import addIcon from 'assets/images/add_icon.svg';
import pencilEditIcon from 'assets/images/ai_new_chat.svg';
import dotVerticalIcon from 'assets/images/dot_vertical.svg';
import loadingJson from 'assets/images/loading.json';
import trashCanIcon from 'assets/images/trash_can.svg';

import './MetricTable.scss';

const loadingOptions = {
  loop: true,
  autoplay: true,
  animationData: loadingJson,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const mapPropsToState = (state) => ({
  selectedStoreIds: state.metricTable.selectedStoreIds,
  selectedLevelIds: state.metricTable.selectedLevelIds,
  timeRangeList: state.metricTable.timeRangeList,
  timeRangeConfigs: state.metricTable.timeRangeConfigs,
  configLoading: state.metricTable.configLoading,
  metricTableData: state.metricTable.metricTableData,
  metricTableDataLoading: state.metricTable.metricTableDataLoading,
});

function MetricTable({
  selectedStoreIds,
  selectedLevelIds,
  timeRangeList,
  timeRangeConfigs,
  configLoading,
  metricTableData,
  metricTableDataLoading,
}) {
  const dispatch = useDispatch();

  const storeCountTitle = React.useMemo(() => {
    if (selectedStoreIds?.length > 0) {
      return `(${selectedStoreIds?.length} Store(s))`;
    }
    return '(All Store(s))';
  }, [selectedStoreIds]);

  const [confirmDeleteStoreIndex, setConfirmDeleteStoreIndex] =
    React.useState(-1);
  const [showDeleteAllStoreModal, setShowDeleteAllStoreModal] =
    React.useState(false);
  const [confirmDeleteRangeIndex, setConfirmDeleteRangeIndex] =
    React.useState(-1);
  const [showDeleteAllRangeModal, setShowDeleteAllRangeModal] =
    React.useState(false);
  const [showSideBar, setShowSideBar] = React.useState(true);
  const [editTimeRangeIndex, setEditTimeRangeIndex] = React.useState(-1);

  const showConfirmModal = React.useMemo(() => {
    return (
      confirmDeleteStoreIndex >= 0 ||
      showDeleteAllStoreModal ||
      confirmDeleteRangeIndex >= 0 ||
      showDeleteAllRangeModal
    );
  }, [
    confirmDeleteStoreIndex,
    showDeleteAllStoreModal,
    confirmDeleteRangeIndex,
    showDeleteAllRangeModal,
  ]);

  const confirmModalTitle = React.useMemo(() => {
    if (confirmDeleteStoreIndex >= 0 || confirmDeleteRangeIndex >= 0) {
      return 'Confirm delete?';
    }
    if (showDeleteAllStoreModal) {
      return 'Clear all store?';
    }
    if (showDeleteAllRangeModal) {
      return 'Clear all custom time range?';
    }
    return '';
  }, [
    confirmDeleteStoreIndex,
    showDeleteAllStoreModal,
    confirmDeleteRangeIndex,
    showDeleteAllRangeModal,
  ]);

  const onConfirmModalClose = () => {
    setConfirmDeleteStoreIndex(-1);
    setShowDeleteAllStoreModal(false);
    setConfirmDeleteRangeIndex(-1);
    setShowDeleteAllRangeModal(false);
  };

  const onConfirmModalConfirmed = () => {
    if (confirmDeleteStoreIndex >= 0) {
      console.log('kevin@86', confirmDeleteStoreIndex);
      deleteStore(confirmDeleteStoreIndex);
    } else if (showDeleteAllStoreModal) {
      console.log('kevin@90', showDeleteAllStoreModal);
      deleteAllStores();
    } else if (confirmDeleteRangeIndex >= 0) {
      console.log('kevin@92', confirmDeleteRangeIndex);
      deleteTimeRange(confirmDeleteRangeIndex);
    } else if (showDeleteAllRangeModal) {
      console.log('kevin@99', showDeleteAllRangeModal);
      deleteOtherTimeRanges();
    }
    onConfirmModalClose();
  };

  React.useEffect(() => {
    dispatch(createAction('metricTable/resetState')());
    dispatch(createAction('metricTable/getMetricTableConfig')());
    return () => {
      dispatch(createAction('metricTable/resetState')());
    };
  }, []);

  const refreshTableData = () => {
    dispatch(createAction('metricTable/getMetricTableData')());
  };

  React.useEffect(() => {
    if (configLoading === 'success') {
      refreshTableData();
    }
  }, [configLoading]);

  const selectStore = (storeId) => {
    console.log('store@177', storeId);
    if (!storeId) {
      return;
    }
    if (metricTableDataLoading) {
      return;
    }
    if (selectedStoreIds?.includes(storeId)) {
      return;
    }
    const newStoreIds = selectedStoreIds.concat([storeId]);
    dispatch(
      createAction('metricTable/updateMetricTableConfig')({
        selectedStoreIds: newStoreIds,
        successAction: () => refreshTableData(),
      }),
    );
  };

  const deleteStore = (storeIndex) => {
    if (metricTableDataLoading) {
      return;
    }
    if (storeIndex < 0 || storeIndex >= selectedStoreIds?.length) {
      return;
    }
    const storeId = selectedStoreIds?.[storeIndex];
    const newStoreIds = selectedStoreIds?.filter((id) => id !== storeId);
    dispatch(
      createAction('metricTable/updateMetricTableConfig')({
        selectedStoreIds: newStoreIds,
        successAction: () => refreshTableData(),
      }),
    );
  };

  const deleteAllStores = () => {
    if (metricTableDataLoading) {
      return;
    }
    if (selectedStoreIds?.length > 0) {
      dispatch(
        createAction('metricTable/updateMetricTableConfig')({
          selectedStoreIds: [],
          successAction: () => refreshTableData(),
        }),
      );
    }
  };

  const deleteTimeRange = (rangeIndex) => {
    if (metricTableDataLoading) {
      return;
    }
    if (rangeIndex < 0 || rangeIndex >= timeRangeList?.length) {
      return;
    }
    const newNames = timeRangeConfigs.filter(
      (name, index) => index !== rangeIndex,
    );
    dispatch(
      createAction('metricTable/updateMetricTableConfig')({
        timeRangeConfigs: newNames,
        successAction: () => refreshTableData(),
      }),
    );
  };

  const deleteOtherTimeRanges = () => {
    if (metricTableDataLoading) {
      return;
    }
    dispatch(
      createAction('metricTable/updateMetricTableConfig')({
        timeRangeConfigs: timeRangeConfigs.splice(0, 1),
        successAction: () => refreshTableData(),
      }),
    );
  };

  const exportMetricTable = () => {
    if (metricTableDataLoading) {
      return;
    }
    const startDates = timeRangeList?.map(
      (timeRange) => timeRange?.[0] ?? null,
    );
    const endDates = timeRangeList?.map((timeRange) => timeRange?.[1] ?? null);
    dispatch(
      createAction('downloadAndImport/createDownloadTask')({
        from: ImportResource.metricTable,
        related: {
          startDates,
          endDates,
          rangeNames: timeRangeConfigs?.map((config) => config?.name),
          stores: selectedStoreIds ?? [],
          levels: selectedLevelIds ?? [],
        },
      }),
    );
  };

  const buttons = [
    <ListButtonsGroup
      hideExtraButtonWidth={580}
      hideAllButtonWidth={580}
      primaryButton={
        <AuthButton
          title="Export"
          className="btn-back-button-common btn-download"
          action={() => {
            exportMetricTable();
          }}
        />
      }
    />,
  ];

  const tabs = [
    {
      name: '',
      content: (
        <div className="d-flex flex-column align-items-stretch table-container">
          <Table className="total-table" responsive striped bordered>
            <thead>
              <tr>
                <th>
                  <div className="d-flex flex-row justify-content-start align-items-center">
                    <span className="table-title">Store</span>
                  </div>
                </th>
                <th>
                  <div className="d-flex flex-row justify-content-start align-items-center">
                    <span className="table-title">Metrics</span>
                  </div>
                </th>
                {timeRangeList?.map((timeRange, index) => {
                  const timeRangeName = timeRangeConfigs?.[index]?.name;
                  return (
                    <th key={`time-range-${index}`}>
                      <div className="d-flex flex-row-space-between flex-gap-8">
                        <div className="d-flex flex-column">
                          <span className="table-title">{timeRangeName}</span>
                          <span className="time-range">
                            {timeRange?.[0]} ~ {timeRange?.[1]}
                          </span>
                        </div>
                        <div className="d-flex flex-row justify-content-end align-items-center flex-gap-16 action-buttons">
                          <Image
                            src={pencilEditIcon}
                            className="not-drag edit-btn"
                            onClick={() => setEditTimeRangeIndex(index)}
                          />
                          {index !== 0 ? (
                            <Image
                              src={trashCanIcon}
                              className="not-drag edit-btn"
                              onClick={() => setConfirmDeleteRangeIndex(index)}
                            />
                          ) : null}
                        </div>
                      </div>
                    </th>
                  );
                })}
                <th>
                  <div className="d-flex flex-row-space-between align-items-center">
                    <div
                      className="d-flex flex-row justify-content-start align-items-center custom-add-button add-time-range-button"
                      onClick={() =>
                        setEditTimeRangeIndex(timeRangeConfigs?.length ?? 0)
                      }
                    >
                      <Image src={addIcon} className="not-drag add-btn" />
                      Add Time Range
                    </div>
                    <Dropdown className="table-dropdown">
                      <Dropdown.Toggle>
                        <Image
                          src={dotVerticalIcon}
                          className="not-drag edit-btn"
                        />
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onSelect={(eventKey) =>
                            setShowDeleteAllRangeModal(true)
                          }
                        >
                          Clear all custom time range
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </th>
              </tr>
            </thead>
            {metricTableDataLoading ? (
              <tbody>
                <tr>
                  <td className="white-background">
                    <span className="table-row-title">Total</span>
                    <br />
                    <span className="table-row-title">{storeCountTitle}</span>
                  </td>
                  <td
                    className="loading-cell"
                    colSpan={(timeRangeList?.length ?? 1) + 2}
                  >
                    <div className="loading-circle">
                      <Lottie
                        options={loadingOptions}
                        style={{}}
                        height={40}
                        width={40}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              metricTableData?.tables?.map((tableData, index) => {
                const tableName = metricTableData?.tableNames?.[index];
                return (
                  <MetricTableBody
                    key={`${tableName}`}
                    tableIndex={index}
                    tableName={tableName}
                    tableData={tableData}
                    onDeleteStore={() =>
                      setConfirmDeleteStoreIndex(Number(index) - 1)
                    }
                  />
                );
              })
            )}
            {/*<tbody>*/}
            {/*  <tr>*/}
            {/*    <td colSpan={(timeRangeList?.length ?? 0) + 2}></td>*/}
            {/*    <td></td>*/}
            {/*  </tr>*/}
            {/*</tbody>*/}
          </Table>
          <div className="d-flex flex-row-space-between align-items-center total-table action-bar">
            <div className="d-flex flex-row justify-content-start align-items-center flex-gap-8 left-buttons">
              <div className="d-flex flex-row justify-content-start align-items-center custom-add-button add-store-button">
                <Image src={addIcon} className="not-drag add-btn" />
                Add Store
              </div>
              <StoreSelectDropdown
                hideDeleteButton
                selectStore={(store) => selectStore(store?.pk)}
                customItemDisabled={(item) =>
                  selectedStoreIds.includes(item?.pk ?? 0)
                }
                hideAddButton
                defaultFilter={{ isSimpleNode: true, active: 'true' }}
              />
              <Dropdown className="table-dropdown">
                <Dropdown.Toggle>
                  <Image src={dotVerticalIcon} className="not-drag edit-btn" />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onSelect={(eventKey) => setShowDeleteAllStoreModal(true)}
                  >
                    Clear all store
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <MetricTableSideBar
        showSideBar={showSideBar}
        setShowSideBar={setShowSideBar}
      />
      <div
        className={`d-flex flex-column metric-table-container ${
          showSideBar ? '' : 'wide'
        }`}
      >
        <CustomListComponent
          caution={{
            detail: '',
            title: 'Metrics Table',
          }}
          defaultActiveKey={tabs[0].name}
          breadcrumb={<></>}
          buttons={buttons}
          tabs={tabs}
          hideTab={true}
        />
      </div>
      <AddTableTimeRange
        editTimeRangeIndex={editTimeRangeIndex}
        onClose={() => setEditTimeRangeIndex(-1)}
      />

      <BasePrompt
        customClass={'export-report-modal'}
        show={showConfirmModal}
        title={confirmModalTitle}
        closeAction={() => onConfirmModalClose()}
        leftButton={{
          text: 'Cancel',
          action: () => onConfirmModalClose(),
        }}
        rightButton={{
          text: 'Confirm',
          disabled: false,
          action: () => onConfirmModalConfirmed(),
        }}
      />
    </>
  );
}

export default connect(mapPropsToState)(MetricTable);
